import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Ban,
  Edit3,
  Loader2,
  Plus,
  Save,
  ShieldCheck,
  Trash2,
  Undo2,
} from "lucide-react";
import { toast } from "sonner";
import { type Carrier, useGetCarriers } from "@/api/carriers";
import {
  type AdminCarrierCustomerConnection,
  type AdminCarrierCustomerTier,
  useAdminCarrierCustomerTiers,
  useAdminCarrierCustomers,
  useCreateAdminCarrierCustomerTier,
  useDeleteAdminCarrierCustomerTier,
  usePatchAdminCarrierCustomer,
  useUpdateAdminCarrierCustomerTier,
} from "@/api/customer-carrier-discounts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  useCarrierScope,
  useCenterScopeNotApplicable,
} from "@/contexts/carrier-scope-context";
import { cn } from "@/lib/utils";

import { GrantCustomerAccess } from "@/features/carriers/grant-customer-access-dialog";

type Filter = "all" | "active" | "left" | "blocked";

interface AppErrorBody {
  code?: string;
  message?: string;
  details?: { assignedCustomerCount?: number };
}

const FILTERS: Array<{ key: Filter; label: string }> = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "left", label: "Left" },
  { key: "blocked", label: "Blocked" },
];

const EMPTY_CUSTOMERS: AdminCarrierCustomerConnection[] = [];
const EMPTY_TIERS: AdminCarrierCustomerTier[] = [];

function sortManagedCarriers(carriers: Carrier[]): Carrier[] {
  return [...carriers].sort((a, b) => {
    const aPrimary = a.code === "ez";
    const bPrimary = b.code === "ez";
    if (aPrimary && !bPrimary) return -1;
    if (!aPrimary && bPrimary) return 1;
    return a.name.localeCompare(b.name);
  });
}

function formatPct(value: number | null | undefined) {
  if (value == null) return "0%";
  return `${Number.isInteger(value) ? value.toFixed(0) : value.toFixed(2)}%`;
}

function customerName(row: AdminCarrierCustomerConnection) {
  const name = [row.customer.firstName, row.customer.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || row.customer.email || row.customerId;
}

function rowState(row: AdminCarrierCustomerConnection): Exclude<Filter, "all"> {
  if (row.status === "BLOCKED") return "blocked";
  if (row.removed) return "left";
  return "active";
}

function stateBadge(row: AdminCarrierCustomerConnection) {
  const state = rowState(row);
  const variant = state === "blocked" ? "destructive" : "outline";
  const className =
    state === "active"
      ? "border-emerald-300 bg-emerald-50 text-emerald-700"
      : state === "left"
        ? "border-amber-300 bg-amber-50 text-amber-700"
        : undefined;
  const label =
    state === "left" ? "Left" : state === "blocked" ? "Blocked" : "Active";
  return (
    <Badge variant={variant} className={className}>
      {label}
    </Badge>
  );
}

function assignedDeleteMessage(error: unknown) {
  if (!axios.isAxiosError<AppErrorBody>(error)) return null;
  const count = error.response?.data?.details?.assignedCustomerCount;
  if (typeof count === "number") {
    return `Reassign ${count} assigned customer${count === 1 ? "" : "s"} before deleting this tier.`;
  }
  return error.response?.data?.message ?? null;
}

function errorMessage(error: unknown) {
  if (axios.isAxiosError<AppErrorBody>(error)) {
    return error.response?.data?.message ?? error.message;
  }
  return error instanceof Error ? error.message : "Request failed";
}

export function CarrierCustomerDiscountsScreen() {
  useCenterScopeNotApplicable();

  const carriers = useGetCarriers({ active: true });
  const { carrierIds } = useCarrierScope();
  const [selectedCarrierId, setSelectedCarrierId] = useState("");

  const managedCarriers = useMemo(
    () => sortManagedCarriers(carriers.data?.data ?? []),
    [carriers.data]
  );

  useEffect(() => {
    if (managedCarriers.length === 0) return;
    if (managedCarriers.some((carrier) => carrier.id === selectedCarrierId)) {
      return;
    }
    const scopedCandidate = carrierIds.find((id) =>
      managedCarriers.some((carrier) => carrier.id === id)
    );
    setSelectedCarrierId(scopedCandidate ?? managedCarriers[0].id);
  }, [carrierIds, managedCarriers, selectedCarrierId]);

  const selectedCarrier =
    managedCarriers.find((carrier) => carrier.id === selectedCarrierId) ?? null;
  const carrierId = selectedCarrier?.id ?? null;
  const customers = useAdminCarrierCustomers(carrierId);
  const canManageCommercialTerms = selectedCarrier?.selfRated === true;
  const tiers = useAdminCarrierCustomerTiers(
    canManageCommercialTerms ? carrierId : null
  );

  const customerRows = customers.data ?? EMPTY_CUSTOMERS;
  const tierRows = tiers.data ?? EMPTY_TIERS;
  const counts = useMemo(
    () => ({
      active: customerRows.filter((row) => rowState(row) === "active").length,
      left: customerRows.filter((row) => rowState(row) === "left").length,
      blocked: customerRows.filter((row) => rowState(row) === "blocked").length,
    }),
    [customerRows]
  );

  return (
    <div className="space-y-6 p-4 lg:p-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h1 className="text-3xl font-bold">Carrier Customers</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Manage customer access and carrier-managed discounts as an EZX
            admin.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          {selectedCarrier && (
            <Badge variant="outline" className="h-9 rounded-md px-3">
              <ShieldCheck className="h-4 w-4" />
              Managing on behalf of {selectedCarrier.name}
            </Badge>
          )}
          <CarrierPicker
            carriers={managedCarriers}
            selectedCarrierId={selectedCarrierId}
            isLoading={carriers.isLoading}
            onChange={setSelectedCarrierId}
          />
        </div>
      </div>

      {carriers.isLoading ? (
        <Loading label="carriers" />
      ) : managedCarriers.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            No carriers are available.
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <MetricCard label="Active" value={counts.active} />
            <MetricCard label="Left" value={counts.left} />
            <MetricCard label="Blocked" value={counts.blocked} />
            {canManageCommercialTerms && (
              <MetricCard label="Tiers" value={tierRows.length} />
            )}
          </div>

          <Tabs
            key={selectedCarrierId}
            defaultValue="customers"
            className="space-y-4"
          >
            <TabsList>
              <TabsTrigger value="customers">Customers</TabsTrigger>
              {canManageCommercialTerms && (
                <TabsTrigger value="tiers">Tiers</TabsTrigger>
              )}
            </TabsList>
            <TabsContent value="customers">
              <CustomersTab
                carrierId={selectedCarrierId}
                carrierName={selectedCarrier?.name ?? ""}
                canManageCommercialTerms={canManageCommercialTerms}
                customerCanToggle={selectedCarrier?.customerCanToggle !== false}
                rows={customerRows}
                tiers={tierRows}
                isLoading={
                  customers.isLoading ||
                  (canManageCommercialTerms && tiers.isLoading)
                }
                error={customers.error ?? tiers.error}
              />
            </TabsContent>
            {canManageCommercialTerms && (
              <TabsContent value="tiers">
                <TiersTab
                  carrierId={selectedCarrierId}
                  tiers={tierRows}
                  isLoading={tiers.isLoading}
                  error={tiers.error}
                />
              </TabsContent>
            )}
          </Tabs>
        </>
      )}
    </div>
  );
}

function CarrierPicker({
  carriers,
  selectedCarrierId,
  isLoading,
  onChange,
}: {
  carriers: Carrier[];
  selectedCarrierId: string;
  isLoading: boolean;
  onChange: (carrierId: string) => void;
}) {
  return (
    <label className="text-sm font-medium">
      Carrier
      <select
        className="mt-1 h-9 w-full min-w-[240px] rounded-md border bg-background px-3 text-sm disabled:opacity-60"
        value={selectedCarrierId}
        disabled={isLoading || carriers.length === 0}
        onChange={(event) => onChange(event.target.value)}
        aria-label="Managed carrier"
      >
        {carriers.map((carrier) => (
          <option key={carrier.id} value={carrier.id}>
            {carrier.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function MetricCard({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent className="py-5">
        <div className="text-2xl font-semibold">{value}</div>
        <div className="text-sm text-muted-foreground">{label}</div>
      </CardContent>
    </Card>
  );
}

function CustomersTab({
  canManageCommercialTerms,
  customerCanToggle,
  carrierId,
  carrierName,
  rows,
  tiers,
  isLoading,
  error,
}: {
  canManageCommercialTerms: boolean;
  customerCanToggle: boolean;
  carrierId: string;
  carrierName: string;
  rows: AdminCarrierCustomerConnection[];
  tiers: AdminCarrierCustomerTier[];
  isLoading: boolean;
  error: unknown;
}) {
  const [filter, setFilter] = useState<Filter>("all");
  const [blockTarget, setBlockTarget] =
    useState<AdminCarrierCustomerConnection | null>(null);
  const patchCustomer = usePatchAdminCarrierCustomer();

  const filteredRows = rows.filter((row) =>
    filter === "all" ? true : rowState(row) === filter
  );

  if (isLoading) return <Loading label="customers" />;
  if (error) return <LoadError label="customers" error={error} />;

  return (
    <div className="space-y-4">
      <GrantCustomerAccess
        key={carrierId}
        carrierId={carrierId}
        carrierName={carrierName}
        connections={rows}
      />
      <div className="inline-flex rounded-md border bg-background p-1">
        {FILTERS.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setFilter(item.key)}
            className={cn(
              "rounded px-3 py-1.5 text-sm",
              filter === item.key
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Tier</TableHead>
                <TableHead>Effective</TableHead>
                <TableHead className="text-right">Access</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-muted-foreground">
                    No customers in this view.
                  </TableCell>
                </TableRow>
              )}
              {filteredRows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <div className="font-medium">{customerName(row)}</div>
                    <div className="text-xs text-muted-foreground">
                      {row.customer.email ?? row.customerId}
                    </div>
                  </TableCell>
                  <TableCell>{stateBadge(row)}</TableCell>
                  <TableCell>
                    <select
                      className="h-9 w-[180px] rounded-md border bg-background px-2 text-sm disabled:opacity-60"
                      value={row.tierId ?? ""}
                      disabled={
                        patchCustomer.isPending || !canManageCommercialTerms
                      }
                      onChange={(event) => {
                        patchCustomer.mutate(
                          {
                            carrierId,
                            customerId: row.customerId,
                            tierId: event.target.value || null,
                          },
                          {
                            onError: (mutationError) =>
                              toast.error("Save failed", {
                                description: errorMessage(mutationError),
                              }),
                          }
                        );
                      }}
                      aria-label={`Tier for ${customerName(row)}`}
                    >
                      <option value="">No tier</option>
                      {tiers.map((tier) => (
                        <option key={tier.id} value={tier.id}>
                          {tier.name}
                        </option>
                      ))}
                    </select>
                  </TableCell>
                  <TableCell className="font-mono">
                    {row.effectiveDiscountPercentage == null
                      ? "Excluded"
                      : formatPct(row.effectiveDiscountPercentage)}
                  </TableCell>
                  <TableCell className="text-right">
                    {/* EZX's own revoke. Separate control because it is a
                        separate actor: the carrier blocking a customer and EZX
                        revoking the pair are different decisions, and lifting
                        one must not lift the other. */}
                    <Button
                      size="sm"
                      variant={row.adminBlocked ? "outline" : "ghost"}
                      className="mr-2"
                      title={
                        !customerCanToggle && !row.adminBlocked
                          ? "Unlock this carrier before revoking customer access"
                          : undefined
                      }
                      disabled={
                        patchCustomer.isPending ||
                        (!customerCanToggle && !row.adminBlocked)
                      }
                      onClick={() =>
                        patchCustomer.mutate(
                          {
                            carrierId,
                            customerId: row.customerId,
                            adminBlock: { blocked: !row.adminBlocked },
                          },
                          {
                            onSuccess: () =>
                              toast.success(
                                row.adminBlocked
                                  ? "Access restored by EZX"
                                  : "Access revoked by EZX"
                              ),
                            onError: (mutationError) =>
                              toast.error("Could not change EZX access", {
                                description: errorMessage(mutationError),
                              }),
                          }
                        )
                      }
                    >
                      {row.adminBlocked ? "Restore (EZX)" : "Revoke (EZX)"}
                    </Button>
                    {canManageCommercialTerms &&
                      (row.status === "BLOCKED" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={patchCustomer.isPending}
                          onClick={() =>
                            patchCustomer.mutate(
                              {
                                carrierId,
                                customerId: row.customerId,
                                block: { blocked: false },
                              },
                              {
                                onSuccess: () =>
                                  toast.success("Customer unblocked"),
                                onError: (mutationError) =>
                                  toast.error("Unblock failed", {
                                    description: errorMessage(mutationError),
                                  }),
                              }
                            )
                          }
                        >
                          <Undo2 className="h-4 w-4" />
                          Unblock
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={patchCustomer.isPending}
                          onClick={() => setBlockTarget(row)}
                        >
                          <Ban className="h-4 w-4" />
                          Block
                        </Button>
                      ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <BlockCustomerDialog
        carrierId={carrierId}
        target={blockTarget}
        onOpenChange={(open) => {
          if (!open) setBlockTarget(null);
        }}
      />
    </div>
  );
}

function BlockCustomerDialog({
  carrierId,
  target,
  onOpenChange,
}: {
  carrierId: string;
  target: AdminCarrierCustomerConnection | null;
  onOpenChange: (open: boolean) => void;
}) {
  const [reason, setReason] = useState("");
  const patchCustomer = usePatchAdminCarrierCustomer();

  useEffect(() => {
    if (!target) setReason("");
  }, [target]);

  return (
    <Dialog open={!!target} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Block {target ? customerName(target) : "customer"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Textarea
            className="min-h-24"
            value={reason}
            maxLength={500}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Reason visible to admins"
            aria-label="Block reason"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={!target || patchCustomer.isPending}
            onClick={() => {
              if (!target) return;
              patchCustomer.mutate(
                {
                  carrierId,
                  customerId: target.customerId,
                  block: {
                    blocked: true,
                    reason: reason.trim() || null,
                  },
                },
                {
                  onSuccess: () => {
                    toast.success("Customer blocked");
                    setReason("");
                    onOpenChange(false);
                  },
                  onError: (mutationError) =>
                    toast.error("Block failed", {
                      description: errorMessage(mutationError),
                    }),
                }
              );
            }}
          >
            <Ban className="h-4 w-4" />
            Block customer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TiersTab({
  carrierId,
  tiers,
  isLoading,
  error,
}: {
  carrierId: string;
  tiers: AdminCarrierCustomerTier[];
  isLoading: boolean;
  error: unknown;
}) {
  const [form, setForm] = useState({
    name: "",
    sortOrder: "0",
  });
  const [editing, setEditing] = useState<AdminCarrierCustomerTier | null>(null);
  const [deleteMessage, setDeleteMessage] = useState<string | null>(null);
  const createTier = useCreateAdminCarrierCustomerTier();
  const updateTier = useUpdateAdminCarrierCustomerTier();
  const deleteTier = useDeleteAdminCarrierCustomerTier();

  if (isLoading) return <Loading label="tiers" />;
  if (error) return <LoadError label="tiers" error={error} />;

  const saveTier = () => {
    const payload = {
      carrierId,
      name: form.name.trim(),
      sortOrder: Number.parseInt(form.sortOrder, 10) || 0,
    };
    if (!payload.name) {
      toast.error("Validation", {
        description: "Tier name is required.",
      });
      return;
    }

    if (editing) {
      updateTier.mutate(
        { tierId: editing.id, ...payload },
        {
          onSuccess: () => {
            toast.success("Tier saved");
            setEditing(null);
            setForm({ name: "", sortOrder: "0" });
          },
          onError: (mutationError) =>
            toast.error("Save failed", {
              description: errorMessage(mutationError),
            }),
        }
      );
      return;
    }

    createTier.mutate(payload, {
      onSuccess: () => {
        toast.success("Tier created");
        setForm({ name: "", sortOrder: "0" });
      },
      onError: (mutationError) =>
        toast.error("Create failed", {
          description: errorMessage(mutationError),
        }),
    });
  };

  const isSaving = createTier.isPending || updateTier.isPending;

  return (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-[360px_1fr]">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {editing ? "Edit tier" : "Create tier"}
          </CardTitle>
          <CardDescription>
            Admin changes are recorded against the selected carrier.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Label className="block">
            Name
            <Input
              className="mt-1"
              value={form.name}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
            />
          </Label>
          <Label className="block">
            Sort order
            <Input
              className="mt-1"
              type="number"
              value={form.sortOrder}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  sortOrder: event.target.value,
                }))
              }
            />
          </Label>
          <div className="flex gap-2">
            <Button disabled={isSaving} onClick={saveTier}>
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : editing ? (
                <Save className="h-4 w-4" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              {editing ? "Save tier" : "Create tier"}
            </Button>
            {editing && (
              <Button
                variant="outline"
                onClick={() => {
                  setEditing(null);
                  setForm({
                    name: "",
                    sortOrder: "0",
                  });
                }}
              >
                Cancel
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Discount tiers</CardTitle>
          <CardDescription>
            Assigned tiers must be cleared before deletion.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {deleteMessage && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {deleteMessage}
            </div>
          )}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Assigned</TableHead>
                <TableHead>Sort</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tiers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-muted-foreground">
                    No tiers configured.
                  </TableCell>
                </TableRow>
              )}
              {tiers.map((tier) => (
                <TableRow key={tier.id}>
                  <TableCell className="font-medium">{tier.name}</TableCell>
                  <TableCell>{tier._count?.connections ?? 0}</TableCell>
                  <TableCell>{tier.sortOrder}</TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditing(tier);
                          setForm({
                            name: tier.name,
                            sortOrder: String(tier.sortOrder),
                          });
                        }}
                      >
                        <Edit3 className="h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={deleteTier.isPending}
                        onClick={() => {
                          setDeleteMessage(null);
                          deleteTier.mutate(
                            { carrierId, tierId: tier.id },
                            {
                              onSuccess: () => toast.success("Tier deleted"),
                              onError: (mutationError) => {
                                const message =
                                  assignedDeleteMessage(mutationError) ??
                                  errorMessage(mutationError);
                                setDeleteMessage(message);
                                toast.error("Delete failed", {
                                  description: message,
                                });
                              },
                            }
                          );
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function Loading({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 rounded-md border bg-muted/30 p-4 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      Loading {label}
    </div>
  );
}

function LoadError({ label, error }: { label: string; error: unknown }) {
  return (
    <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
      Could not load {label}: {errorMessage(error)}
    </div>
  );
}
